import {
    BobcardCallStatusEnum,
    BobcardEventTypes,
    BOBCARD__EVENT__CALLS__CALL_START_TIME__COLUMN_ID,
    BOBCARD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    BOBCARD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    BOBCARD__EVENT__CALLS__STATUS__COLUMN_ID,
    BOBCARD__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
} from "~/domainModels/workspaceSpecific/bobcard";
import {
    buildScheduleCallTimeSlotOptions,
    combineScheduleDateAndTimeSlot,
    countJourneyEventsByTemplate,
} from "~/domainModels/scheduleCall/scheduleCallShared";
import type {ScheduleCallStrategy} from "~/domainModels/scheduleCall/scheduleCallStrategy";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";

const BOBCARD_SCHEDULE_CALL_UTM_PLATFORM = "ottopilot";

function journeyContactNumber(journey: EventContainerWithEvents): string {
    const raw = journey.additionalColumnValues[BOBCARD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID];
    return typeof raw === "string" ? raw.trim() : String(raw ?? "").trim();
}

function journeyCustomerName(journey: EventContainerWithEvents): string {
    const raw = journey.additionalColumnValues[BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID];
    return typeof raw === "string" ? raw.trim() : String(raw ?? "").trim();
}

export const bobcardScheduleCallStrategy: ScheduleCallStrategy = {
    callEventTemplateId: BobcardEventTypes.call as Uuid,
    supportsInlineModal: true,
    resolveCallEventTemplate: (eventTemplates) =>
        eventTemplates.find((template) => template.id === (BobcardEventTypes.call as Uuid)),
    buildCallTimeSlotOptions: buildScheduleCallTimeSlotOptions,
    combineDateAndTimeSlot: combineScheduleDateAndTimeSlot,
    buildSubmitPayload: ({journey, scheduledOnIso, workspaceUserId, now}) => {
        const phoneNumber = journeyContactNumber(journey);
        if (phoneNumber === "") {
            throw new Error("Contact number is required on the lead before scheduling a call.");
        }

        const callTemplateId = BobcardEventTypes.call as Uuid;
        const existingCallEventsCount = countJourneyEventsByTemplate(journey, callTemplateId);
        const customerName = journeyCustomerName(journey);

        const journeyAdditionalColumnValues: Record<Uuid, unknown> = {
            ...journey.additionalColumnValues,
            [BOBCARD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID]: callTemplateId,
            [BOBCARD__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID]: now,
            [BOBCARD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID]: existingCallEventsCount + 1,
            [BOBCARD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID]: phoneNumber,
            ...(customerName !== "" ? {[BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID]: customerName} : {}),
        };

        return {
            journeyUpdate: {
                additionalColumnValues: journeyAdditionalColumnValues,
            },
            eventAdditionalColumnValues: {
                [BOBCARD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID]: workspaceUserId,
                [BOBCARD__EVENT__CALLS__STATUS__COLUMN_ID]: BobcardCallStatusEnum.SCHEDULED,
                [BOBCARD__EVENT__CALLS__CALL_START_TIME__COLUMN_ID]: scheduledOnIso,
                [BOBCARD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID]: now,
                [BOBCARD__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID]: BOBCARD_SCHEDULE_CALL_UTM_PLATFORM,
            },
        };
    },
};
